/**
 * Realistic vehicle physics engine.
 *
 * Instead of simple linear interpolation between two GPS points,
 * this engine simulates how a real vehicle moves:
 *
 * - Follows road geometry (from Valhalla) instead of straight lines
 * - Applies acceleration / deceleration curves (not instant speed changes)
 * - Derives heading from road tangent, not point-to-point bearing
 * - Handles speed transitions smoothly (approaching stops, speeding up)
 * - Clamps speed to road speed limits when available
 */

// ── Constants ────────────────────────────────────────────────────────────────

// Typical passenger vehicle dynamics (conservative values)
const DEFAULT_ACCEL_MS2 = 2.5;   // m/s² — comfortable acceleration
const DEFAULT_DECEL_MS2 = 3.0;   // m/s² — comfortable braking
const MAX_LATERAL_ACCEL = 4.0;   // m/s² — max cornering before discomfort
const MIN_SPEED_THRESHOLD = 0.5; // mph — below this, treat as stopped

// ── Helpers ──────────────────────────────────────────────────────────────────

function mphToMs(mph) { return mph * 0.44704; }
function msToMph(ms) { return ms * 2.23694; }

function deg2rad(deg) { return deg * (Math.PI / 180); }

function haversineMeters(a, b) {
  const R = 6371000;
  const dLat = deg2rad(b.lat - a.lat);
  const dLng = deg2rad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat +
    Math.cos(deg2rad(a.lat)) * Math.cos(deg2rad(b.lat)) * sinLng * sinLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// Bearing from `from` to `to` in degrees [0, 360)
function bearing(from, to) {
  const dLng = deg2rad(to.lng - from.lng);
  const fromLat = deg2rad(from.lat);
  const toLat = deg2rad(to.lat);
  const y = Math.sin(dLng) * Math.cos(toLat);
  const x = Math.cos(fromLat) * Math.sin(toLat) -
    Math.sin(fromLat) * Math.cos(toLat) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

// Shortest angular difference, signed
function angleDiff(a, b) {
  let diff = b - a;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return diff;
}

// Normalize angle to [0, 360)
function normalizeAngle(a) {
  return ((a % 360) + 360) % 360;
}

// ── Road-Following Interpolation ─────────────────────────────────────────────

/**
 * Given a road geometry path (array of {lat, lng}), compute cumulative
 * distance at each vertex. Used for distance-based interpolation.
 */
function computePathDistances(path) {
  const dists = [0];
  for (let i = 1; i < path.length; i++) {
    dists.push(dists[i - 1] + haversineMeters(path[i - 1], path[i]));
  }
  return dists;
}

/**
 * Interpolate a position along a road geometry path at a given distance.
 * Returns {lat, lng, heading, segmentIndex}.
 */
function interpolateAlongPath(path, pathDistances, targetDistance) {
  const totalDist = pathDistances[pathDistances.length - 1];
  const clamped = Math.max(0, Math.min(targetDistance, totalDist));

  // Find the segment
  let idx = 0;
  for (let i = 1; i < pathDistances.length; i++) {
    if (pathDistances[i] >= clamped) {
      idx = i - 1;
      break;
    }
    if (i === pathDistances.length - 1) {
      idx = i - 1;
    }
  }

  const segStart = pathDistances[idx];
  const segEnd = pathDistances[idx + 1] || segStart + 1;
  const segLen = segEnd - segStart;
  const t = segLen > 0 ? (clamped - segStart) / segLen : 0;

  const p0 = path[idx];
  const p1 = path[idx + 1] || p0;

  return {
    lat: p0.lat + (p1.lat - p0.lat) * t,
    lng: p0.lng + (p1.lng - p0.lng) * t,
    heading: bearing(p0, p1),
    segmentIndex: idx,
  };
}

// ── Speed Profile Generation ─────────────────────────────────────────────────

/**
 * Build a realistic speed profile along the animation segment.
 *
 * Given start speed, end speed, distance, and duration, compute a speed
 * value at any progress fraction [0, 1] that:
 * - Accelerates/decelerates within physical limits
 * - Smoothly transitions (no teleporting to a new speed)
 * - Respects road speed limits when available
 */
function buildSpeedProfile(startSpeedMph, endSpeedMph, distanceMeters, durationMs, roadSpeedLimitMph) {
  const v0 = mphToMs(Math.max(startSpeedMph, 0));
  const v1 = mphToMs(Math.max(endSpeedMph, 0));
  const vLimit = roadSpeedLimitMph != null ? mphToMs(roadSpeedLimitMph) : Infinity;
  const T = durationMs / 1000; // total time in seconds

  if (T <= 0 || distanceMeters <= 0) {
    return (_fraction) => startSpeedMph;
  }

  // Determine cruise speed: the average speed needed to cover distance in time
  const vCruise = Math.min(distanceMeters / T, vLimit);

  // Time budgets for accel / decel / cruise phases
  const accelRate = DEFAULT_ACCEL_MS2;
  const decelRate = DEFAULT_DECEL_MS2;

  // Time to accelerate from v0 to vCruise
  const tAccel = Math.abs(vCruise - v0) / accelRate;
  // Time to decelerate from vCruise to v1
  const tDecel = Math.abs(vCruise - v1) / decelRate;
  // Time cruising
  const tCruise = Math.max(0, T - tAccel - tDecel);

  // If accel + decel exceed total time, scale them down proportionally
  const tTotal = tAccel + tDecel;
  const scale = tTotal > T ? T / tTotal : 1;
  const adjTAccel = tAccel * scale;
  const adjTDecel = tDecel * scale;

  return (fraction) => {
    const t = fraction * T; // time in seconds

    let speed;
    if (t <= adjTAccel && adjTAccel > 0) {
      // Acceleration phase
      const accelFrac = t / adjTAccel;
      speed = v0 + (vCruise - v0) * smoothStep(accelFrac);
    } else if (t >= T - adjTDecel && adjTDecel > 0) {
      // Deceleration phase
      const decelFrac = (t - (T - adjTDecel)) / adjTDecel;
      speed = vCruise + (v1 - vCruise) * smoothStep(decelFrac);
    } else {
      // Cruise phase
      speed = vCruise;
    }

    return msToMph(Math.max(0, Math.min(speed, vLimit)));
  };
}

// Smooth step for natural transitions (Hermite interpolation)
function smoothStep(t) {
  const clamped = Math.max(0, Math.min(1, t));
  return clamped * clamped * (3 - 2 * clamped);
}

// ── Heading Interpolation ────────────────────────────────────────────────────

/**
 * Smooth heading interpolation that:
 * - Blends road geometry heading with GPS heading based on confidence
 * - Takes the shortest rotation path
 * - Applies eased rotation for large turns (realistic steering lag)
 *
 * @param {number} startHeading - GPS heading at segment start
 * @param {number} endHeading - GPS heading at segment end
 * @param {number|null} roadHeading - Road tangent heading from Valhalla
 * @param {number} progress - Animation progress 0–1
 * @param {number} roadConfidence - How much to trust road heading (0–1)
 *   1.0 = full trust in road heading (good match)
 *   0.0 = ignore road, use GPS heading only (bad match / off-road)
 */
function interpolateHeading(startHeading, endHeading, roadHeading, progress, roadConfidence = 1.0) {
  // Blend road heading with GPS heading based on confidence
  // Low confidence → use GPS heading (always ground truth)
  // High confidence → use road tangent (smoother, road-following)
  let targetHeading;
  if (roadHeading != null && roadConfidence > 0) {
    // Weighted blend: at confidence=1.0, use 100% road heading
    // At confidence=0.5, use 50/50 blend. At 0.0, use 100% GPS heading.
    const gpsTarget = endHeading;
    const roadDiff = angleDiff(gpsTarget, roadHeading);
    targetHeading = normalizeAngle(gpsTarget + roadDiff * roadConfidence);
  } else {
    targetHeading = endHeading;
  }

  const diff = angleDiff(startHeading, targetHeading);
  const absDiff = Math.abs(diff);

  if (absDiff < 2) {
    return normalizeAngle(startHeading);
  }

  if (absDiff > 45) {
    // Large turn: delay rotation, then apply quickly (simulates steering response)
    if (progress < 0.7) {
      const driftFrac = progress / 0.7;
      return normalizeAngle(startHeading + diff * 0.15 * smoothStep(driftFrac));
    } else {
      const turnFrac = (progress - 0.7) / 0.3;
      const applied = 0.15 + 0.85 * smoothStep(turnFrac);
      return normalizeAngle(startHeading + diff * applied);
    }
  }

  // Gradual heading change
  return normalizeAngle(startHeading + diff * smoothStep(progress));
}

// ── Main Segment Animator ────────────────────────────────────────────────────

/**
 * Create an animation segment that computes position, heading, and speed
 * at any progress fraction [0, 1].
 *
 * @param {Object} start - Start point {lat, lng, speed, heading, timestamp}
 * @param {Object} end - End point {lat, lng, speed, heading, timestamp}
 * @param {Array|null} roadPath - Road geometry [{lat, lng}] from Valhalla (null = straight line)
 * @param {Object} options - { roadSpeedLimitMph, confidence }
 *   confidence: 0–1, how much to trust the road path (from scoreMatchConfidence)
 *     1.0 = follow road exactly (good match)
 *     0.5 = blend road + straight line (uncertain match)
 *     0.0 = ignore road, straight line with GPS heading (bad match / off-road)
 * @returns {Object} Segment with .at(fraction) method
 */
export function createSegment(start, end, roadPath, options = {}) {
  const straightDist = haversineMeters(start, end);
  const duration = end.timestamp - start.timestamp;
  const confidence = options.confidence != null ? options.confidence : 1.0;

  // Below threshold: don't use road path at all — vehicle is off-road or match is wrong
  const useRoad = roadPath && roadPath.length >= 2 && confidence >= 0.3;

  const path = useRoad ? roadPath : [
    { lat: start.lat, lng: start.lng },
    { lat: end.lat, lng: end.lng },
  ];
  const pathDistances = computePathDistances(path);
  const totalPathDist = pathDistances[pathDistances.length - 1] || straightDist;

  // For medium-confidence matches (0.3–0.7), blend road position with straight line
  const blendRoad = useRoad && confidence < 0.7;
  const straightPath = blendRoad ? [
    { lat: start.lat, lng: start.lng },
    { lat: end.lat, lng: end.lng },
  ] : null;

  const speedProfile = buildSpeedProfile(
    start.speed || 0,
    end.speed || 0,
    totalPathDist,
    duration,
    options.roadSpeedLimitMph
  );

  return {
    duration,
    totalDistance: totalPathDist,
    hasRoadPath: useRoad,
    confidence,

    at(fraction) {
      const f = Math.max(0, Math.min(1, fraction));
      const distAlongPath = f * totalPathDist;

      let pos = interpolateAlongPath(path, pathDistances, distAlongPath);
      const speed = speedProfile(f);

      // Blend road position with straight line for medium-confidence matches
      if (blendRoad && straightPath) {
        const straightPos = {
          lat: straightPath[0].lat + (straightPath[1].lat - straightPath[0].lat) * f,
          lng: straightPath[0].lng + (straightPath[1].lng - straightPath[0].lng) * f,
        };
        // confidence=0.3 → 0% road, 100% straight
        // confidence=0.7 → 100% road, 0% straight
        const roadWeight = (confidence - 0.3) / 0.4;
        pos = {
          lat: straightPos.lat + (pos.lat - straightPos.lat) * roadWeight,
          lng: straightPos.lng + (pos.lng - straightPos.lng) * roadWeight,
          heading: pos.heading,
          segmentIndex: pos.segmentIndex,
        };
      }

      // Pass confidence to heading interpolation:
      // high confidence → trust road heading, low → trust GPS heading
      const heading = interpolateHeading(
        start.heading || 0,
        end.heading || 0,
        useRoad ? pos.heading : null,
        f,
        confidence
      );

      return {
        lat: pos.lat,
        lng: pos.lng,
        speed,
        heading,
        timestamp: start.timestamp + f * duration,
        segmentIndex: pos.segmentIndex,
      };
    },
  };
}

/**
 * Calculate a realistic animation duration for a segment.
 *
 * Unlike the original which just did distance/speed, this accounts for:
 * - Queue depth (catch-up factor when falling behind)
 * - Minimum/maximum duration bounds
 * - Time since the data was generated (latency compensation)
 */
export function calculateRealisticDuration(start, end, queueDepth, latestTimestamp) {
  const timeDelta = end.timestamp - start.timestamp;

  if (timeDelta <= 0) {
    return 500; // minimum fallback
  }

  let duration = timeDelta;

  // If the queue is deep (>2), we're behind — speed up to catch up
  if (queueDepth > 2) {
    // Progressively faster as queue grows, but never < 40% of real time
    const catchUpFactor = Math.max(0.4, 1 - (queueDepth - 2) * 0.15);
    duration *= catchUpFactor;
  }

  // Latency compensation: if the latest data timestamp is ahead of end,
  // we're behind real-time. Compress duration.
  if (latestTimestamp && latestTimestamp > end.timestamp) {
    const lag = latestTimestamp - end.timestamp;
    const lagFactor = Math.max(0.3, 1 - lag / timeDelta * 0.5);
    duration *= lagFactor;
  }

  // Clamp to reasonable bounds
  return Math.max(200, Math.min(duration, 30000));
}

export {
  haversineMeters,
  bearing,
  angleDiff,
  normalizeAngle,
  mphToMs,
  msToMph,
  interpolateHeading,
  smoothStep,
  computePathDistances,
  interpolateAlongPath,
};
